import { Box, Modal } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  AiFillPlusCircle,
  AiOutlineUpload,
  AiFillDelete,
} from "react-icons/ai";
import { useMutation } from "@tanstack/react-query";
import { submitMediaOms, uploadMediaOms } from "../../../../services/oms";
import { RxCross1 } from "react-icons/rx";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 4,
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_COMPRESSED_SIZE = 2 * 1024 * 1024;
const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];
const ALLOWED_VIDEO_EXTENSIONS = ["mp4"];
const MAX_FILES = 5;

const ModalUploadMedia = ({ open, setOpen }) => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [reqBodyImage, setReqBodyImage] = useState({
    order_id: open?.order_id || 0,
    images: [],
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  const fileInputRef = useRef(null);

  const handleClose = () => {
    setOpen(false);
    setErrorMessage("");
    setUploadProgress({});
    setReqBodyImage({
      order_id: 0,
      images: [],
    });
  };

  const { mutate: mutateUploadMedia } = useMutation({
    mutationFn: uploadMediaOms,
  });

  const { mutate: mutateSubmitMedia, isLoading: isLoadingSubmit } = useMutation({
    mutationFn: submitMediaOms,
    onSuccess: () => {
      handleClose()
      setReqBodyImage({ order_id: 0, images: [] })
    },
    onError: (err) => setErrorMessage(err.message.id ? err.message.id : err.message)
  })

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (open?.order_id) {
      setReqBodyImage((prev) => ({
        ...prev,
        order_id: open.order_id,
      }));
    }
  }, [open]);


  const compressImage = (file, maxWidth = 1024, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      if (file.type.startsWith("video/")) {
        resolve(file);
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          const tryCompress = (currentQuality) => {
            canvas.toBlob(
              (blob) => {
                if (blob.size > MAX_COMPRESSED_SIZE && currentQuality > 0.4) {
                  tryCompress(currentQuality - 0.1);
                } else {
                  const compressedFile = new File([blob], file.name, {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                  });
                  resolve(compressedFile);
                }
              },
              "image/jpeg",
              currentQuality
            );
          };

          tryCompress(quality);
        };

        img.onerror = () => {
          reject(new Error("Gagal memuat gambar"));
        };

        img.src = e.target.result;
      };

      reader.onerror = () => {
        reject(new Error("Gagal membaca file"));
      };

      reader.readAsDataURL(file);
    });
  };

  const validateFile = (file) => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const isImage = ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension);
    const isVideo = ALLOWED_VIDEO_EXTENSIONS.includes(fileExtension);

    if (!isImage && !isVideo) {
      return `Format file tidak didukung. Gunakan file dengan format: ${[
        ...ALLOWED_IMAGE_EXTENSIONS,
        ...ALLOWED_VIDEO_EXTENSIONS,
      ].join(", ")}`;
    }

    if (file.size > MAX_FILE_SIZE) {
      return `Ukuran file terlalu besar. Maksimal ${MAX_FILE_SIZE / (1024 * 1024)
        } MB`;
    }

    return null;
  };

  const uploadSingleFile = (file) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();

      formData.append("file", file);
      formData.append("type", "public");
      formData.append("datasource", "complaint");


      mutateUploadMedia(formData, {
        onSuccess: (response) => {
          if (response?.status === "success") {
            if (response?.detail.url) {
              resolve(response.detail.url);
            } else {
              reject(new Error("URL tidak ditemukan dalam response"));
            }
          } else {
            reject(new Error(response?.message || "Upload gagal"));
          }
        },
        onError: (error) => {
          console.error("Upload error:", error);
          reject(
            new Error(
              error.response?.data?.message ||
              error.message ||
              "Terjadi kesalahan saat upload"
            )
          );
        },
      });
    });
  };

  const uploadMultipleFiles = (files) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();

      files.forEach((file, index) => {
        formData.append("file", file);
      });

      formData.append("type", "public");
      formData.append("datasource", "complaint");

      mutateUploadMedia(formData, {
        onSuccess: (response) => {
          if (response?.status === "success") {
            resolve(response.detail || []);
          } else {
            reject(new Error(response?.message || "Upload gagal"));
          }
        },
        onError: (error) => {
          console.error("Multiple upload error:", error);
          reject(
            new Error(
              error.response?.data?.message ||
              error.message ||
              "Terjadi kesalahan saat upload"
            )
          );
        },
      });
    });
  };

  const processAndUploadFiles = async (files) => {
    const fileArray = Array.from(files);

    const totalFilesAfterUpload = reqBodyImage.images.length + fileArray.length;
    if (totalFilesAfterUpload > MAX_FILES) {
      setErrorMessage(
        `Maksimal ${MAX_FILES} file. Anda sudah memiliki ${reqBodyImage.images.length} file.`
      );
      return;
    }

    setUploading(true);
    setErrorMessage("");

    try {
      const processedFiles = [];

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];

        const validationError = validateFile(file);
        if (validationError) {
          setErrorMessage(`${file.name}: ${validationError}`);
          continue;
        }

        let processedFile = file;

        if (file.type.startsWith("image/") && file.size > 1 * 1024 * 1024) {
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: { status: "compressing", progress: 0 },
          }));

          try {
            processedFile = await compressImage(file);
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: { status: "compressed", progress: 0 },
            }));
          } catch (compressError) {
            console.error(`Kompresi gagal untuk ${file.name}:`, compressError);
            setErrorMessage(`Gagal mengkompresi ${file.name}`);
            continue;
          }
        }

        processedFiles.push(processedFile);
      }

      if (processedFiles.length === 0) {
        setUploading(false);
        return;
      }

      try {
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          processedFiles.forEach((file) => {
            newProgress[file.name] = { status: "uploading", progress: 50 };
          });
          return newProgress;
        });

        const uploadedUrls = await uploadMultipleFiles([processedFiles]);

        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          processedFiles.forEach((file) => {
            newProgress[file.name] = { status: "completed", progress: 100 };
          });
          return newProgress;
        });

        setReqBodyImage((prev) => ({
          ...prev,
          images: [...prev.images, ...uploadedUrls],
        }));
      } catch (uploadError) {
        console.error(
          "Multiple upload failed, trying sequential:",
          uploadError
        );

        await uploadFilesSequential(processedFiles);
      }
    } catch (error) {
      console.error("Upload process failed:", error);
      setErrorMessage("Terjadi kesalahan saat memproses file");
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress({});
      }, 2000);
    }
  };

  const uploadFilesSequential = async (processedFiles) => {
    const uploadedUrls = [];

    for (let i = 0; i < processedFiles.length; i++) {
      const file = processedFiles[i];

      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: { status: "uploading", progress: 50 },
      }));

      try {
        const uploadedUrl = await uploadSingleFile(file);
        uploadedUrls.push(uploadedUrl);

        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: { status: "completed", progress: 100 },
        }));

        setReqBodyImage((prev) => ({
          ...prev,
          images: [...prev.images, uploadedUrl],
        }));
      } catch (uploadError) {
        console.error(`Upload gagal untuk ${file.name}:`, uploadError);
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: { status: "error", progress: 0 },
        }));
        setErrorMessage(`Gagal upload ${file.name}: ${uploadError.message}`);
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    return uploadedUrls;
  };

  const handleFileInputChange = (e) => {
    if (e.target.files.length === 0) return;
    const files = e.target.files;
    processAndUploadFiles(files);
    e.target.value = null;
  };

  const handleTakePhoto = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;

    if (
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    ) {
      input.capture = "environment";
    }

    input.addEventListener("change", (e) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processAndUploadFiles(files);
      }
    });

    input.click();
  };

  const handleRemoveFile = (index) => {
    setReqBodyImage((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleFinishUpload = () => {
    if (reqBodyImage.images.length === 0) {
      setErrorMessage("Harus mengupload minimal 1 file");
      return;
    }
    mutateSubmitMedia(reqBodyImage)
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "compressing":
        return { text: "Mengkompresi...", color: "bg-yellow-500" };
      case "uploading":
        return { text: "Mengupload...", color: "bg-blue-500" };
      case "completed":
        return { text: "Selesai", color: "bg-green-500" };
      case "error":
        return { text: "Error", color: "bg-red-500" };
      default:
        return { text: "Memproses...", color: "bg-gray-500" };
    }
  };

  return (
    <>
      <Modal open={Boolean(open)} onClose={handleClose}>
        <Box
          sx={{
            ...style,
            width: isMobileView ? "95%" : "65%",
            maxHeight: "85vh",
            padding: 0,
            overflowY: "auto",
          }}
          className={`text-left text-base ${isMobileView ? "w-full" : "w-3/4 lg:w-1/3"
            }`}
        >
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Upload Media</h2>
                <p className="text-sm text-gray-600">
                  Upload foto atau video untuk keluhan Anda
                </p>
              </div>
              <RxCross1 className="cursor-pointer hover:text-red-500 hover:bg-gray-100 rounded-full p-1 text-[24px]"
                onClick={handleClose} />
            </div>

            {errorMessage && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {errorMessage}
              </div>
            )}

            {true && <input
              hidden
              type="file"
              id="input-media-oms"
              ref={fileInputRef}
              multiple
              accept="image/jpeg, image/png, image/jpg, video/mp4"
              onChange={handleFileInputChange}
            />}

            {reqBodyImage.images.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg w-full h-[250px] hover:border-[#007AF1] transition-colors">
                <div
                  className="border-2 border-[#007AF1] rounded-lg px-8 py-2 hover:cursor-pointer flex items-center mb-2 bg-blue-50 hover:bg-blue-100 transition-colors"
                  onClick={() => { if (!isLoadingSubmit) fileInputRef.current.click() }}
                >
                  <AiFillPlusCircle className="mr-3 text-[32px] text-[#007AF1]" />
                  <p className="font-medium">Upload Foto / Video</p>
                </div>
                <p className="text-sm text-gray-500 text-center">
                  Maksimal {MAX_FILES} file • Format: JPG, PNG, MP4 • Maks. 10MB
                  per file
                </p>
              </div>
            ) : (
              <div className="border-2 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {reqBodyImage.images.map((value, index) => (
                    <div key={index} className="relative group">
                      {value.includes(".mp4") ? (
                        <video
                          className="border border-gray-300 rounded-lg w-full h-32 object-cover"
                          src={value}
                          controls
                        />
                      ) : (
                        <img
                          alt={`Preview ${index + 1}`}
                          className="border border-gray-300 rounded-lg w-full h-32 object-cover"
                          src={value}
                        />
                      )}
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        title="Hapus file"
                        disabled={isLoadingSubmit}
                      >
                        <AiFillDelete size={16} />
                      </button>
                    </div>
                  ))}

                  {reqBodyImage.images.length < MAX_FILES && (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg w-full h-32 flex flex-col items-center justify-center hover:cursor-pointer hover:border-[#007AF1] hover:bg-blue-50 transition-colors"
                      onClick={() => { if (!isLoadingSubmit) fileInputRef.current.click() }}
                    >
                      <AiOutlineUpload className="text-[30px] text-gray-400 mb-1" />
                      <p className="text-sm text-gray-500">Tambah File</p>
                    </div>
                  )}
                </div>
                <div className="mt-3 text-sm text-gray-500 text-center">
                  {reqBodyImage.images.length} / {MAX_FILES} file terupload
                </div>
              </div>
            )}

            {Object.keys(uploadProgress).length > 0 && (
              <div className="mt-4 space-y-3">
                <h4 className="font-medium text-gray-700">Progress Upload:</h4>
                {Object.entries(uploadProgress).map(
                  ([fileName, fileProgress]) => {
                    const statusInfo = getStatusInfo(fileProgress.status);
                    return (
                      <div key={fileName} className="text-sm">
                        <div className="flex justify-between mb-1">
                          <span className="truncate flex-1 mr-2">
                            {fileName}
                          </span>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${fileProgress.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : fileProgress.status === "error"
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                              }`}
                          >
                            {statusInfo.text}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${statusInfo.color}`}
                            style={{ width: `${fileProgress.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}

            <div className="w-full flex flex-col md:flex-row items-center gap-3 justify-end mt-6">
              {isMobileView && (
                <button
                  className="flex items-center justify-center w-full gap-2 cursor-pointer border-2 border-primary text-primary bg-white hover:bg-gray-50 rounded-full px-6 h-12 font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleTakePhoto}
                  type="button"
                  disabled={
                    uploading || reqBodyImage.images.length >= MAX_FILES || isLoadingSubmit
                  }
                >
                  <p>Ambil Foto</p>
                </button>
              )}

              <button
                className="flex items-center justify-center w-full gap-2 cursor-pointer border-2 border-primary text-primary bg-white hover:bg-gray-50 rounded-full px-6 h-10 font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => { if (!isLoadingSubmit) fileInputRef.current.click() }}
                type="button"
                disabled={uploading || reqBodyImage.images.length >= MAX_FILES || isLoadingSubmit}
              >
                <AiOutlineUpload className="text-lg" />
                <p>Pilih File</p>
              </button>

              {reqBodyImage.images.length > 0 && (
                <button
                  className="flex items-center justify-center w-full gap-2 cursor-pointer bg-primary text-white hover:bg-blue-700 rounded-full px-6 h-12 font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleFinishUpload}
                  type="button"
                  disabled={uploading || isLoadingSubmit}
                >
                  {uploading ? "Mengupload..." : "Selesai Upload"}
                </button>
              )}
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default ModalUploadMedia;
